import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Login from './pages/Login'; // Assuming you have these components
import Register from './pages/Register'; // Assuming you have these components
import DashboardLayout from './layouts/DashboardLayout';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import AdminDashboard from './pages/AdminDashboard';
import UserDashboard from './pages/UserDashboard';
// import UserManagement from './pages/UserManagement'; // Comment out or remove old import
import ClubAccountManagement from './pages/ClubAccountManagement'; // Import the renamed component
import AdminList from './pages/AdminList';
import Settings from './pages/Settings';
import Payments from './pages/Payments';
import PrivateRoute from './components/PrivateRoute';
import Statistics from './pages/Statistics';
import ChatbotAgent from './pages/ChatbotAgent';
import VoiceAgent from './pages/VoiceAgent';
import SubscriptionPlanManagement from './pages/SubscriptionPlanManagement';
import NotificationSettings from './components/NotificationSettings';
import AllNotifications from './pages/Notifications';
import AnalyticsReports from './pages/AnalyticsReports';
import UserNotifications from './pages/user/UserNotifications';
import UserSettings from './pages/user/UserSettings';
import UserBilling from './pages/user/UserBilling';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';
// Import new components for Voice Agent sub-menus
import PhoneNumbers from './pages/voice-agent/PhoneNumbers'; // Updated path
import BatchCall from './pages/voice-agent/BatchCall';     // Updated path
import CallHistory from './pages/voice-agent/CallHistory'; // Updated path
import Analytics from './pages/voice-agent/Analytics';     // Updated path
import VoiceAgentKnowledgeBase from './pages/voice-agent/KnowledgeBase'; // New KB specifically for Voice Agent
import ChatbotKnowledgeBase from './pages/chatbot-agent/KnowledgeBase';
import ChatbotAnalytics from './pages/chatbot-agent/Analytics';
import ChatbotTranscription from './pages/chatbot-agent/Transcript';
import UserDashboardLayout from './layouts/UserDashboardLayout';
import ClubManagement from './pages/ClubManagement'; // Import the new page
import ApiKeysPage from './pages/ApiKeys'; // Import the main ApiKeys page with a distinct name
import LeadsManagement from './pages/LeadsManagement';
import DocumentsManagement from './pages/DocumentsManagement';
import WidgetManagement from './pages/WidgetManagement'; // Import the new page
import WidgetPage from './pages/WidgetPage'; // Import the new WidgetPage component
import Home from './pages/home';
import ClubDocumentUpload from './pages/ClubDocumentUpload';
// You might have a PrivateRoute component or similar logic
// to protect the /admin routes. For simplicity here, I'll just structure the routes.
// You should integrate your authentication logic.

const AppRoutes = () => {
  useAuth(); // Called for its side effects or if other hooks depend on it, but currentUser is not used.

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/club/documents/upload/:clubId" element={<ClubDocumentUpload />} />

      {/* User Routes */}
      <Route path="/user" element={
        <PrivateRoute allowedRoles={['user', 'admin', 'superadmin']}>
          <UserDashboardLayout />
        </PrivateRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<UserDashboard />} />
        <Route path="notifications" element={<UserNotifications />} />
        <Route path="settings" element={<UserSettings />} />
        <Route path="billing" element={<UserBilling />} />
        
        {/* Chatbot Agent Routes */}
        <Route path="chatbot-agent" element={<ChatbotAgent />}>
          <Route index element={<Navigate to="analytics" replace />} />
          <Route path="analytics" element={<ChatbotAnalytics />} />
          <Route path="knowledge-base" element={<ChatbotKnowledgeBase />} />
          <Route path="transcript" element={<ChatbotTranscription />} />
        </Route>

        {/* Voice Agent Routes */}
        <Route path="voice-agent" element={<VoiceAgent />}>
          <Route index element={<Navigate to="knowledge-base" replace />} />
          <Route path="knowledge-base" element={<VoiceAgentKnowledgeBase />} />
          <Route path="phone-numbers" element={<PhoneNumbers />} />
          <Route path="batch-call" element={<BatchCall />} />
          <Route path="call-history" element={<CallHistory />} />
          <Route path="analytics" element={<Analytics />} />
        </Route>
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={
        <PrivateRoute allowedRoles={['admin', 'superadmin']}>
          <DashboardLayout />
        </PrivateRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        {/* Super Admin Dashboard route */}
        <Route path="dashboard" element={
          <PrivateRoute allowedRoles={['superadmin']}>
            <SuperAdminDashboard />
          </PrivateRoute>
        } />
        {/* Admin Dashboard route */}
        <Route path="admin-dashboard" element={
          <PrivateRoute allowedRoles={['admin', 'superadmin']}>
            <AdminDashboard />
          </PrivateRoute>
        } />

        {/* Chatbot Agent Routes */}
        <Route path="chatbot-agent" element={<ChatbotAgent />}>
          <Route index element={<Navigate to="analytics" replace />} />
          <Route path="analytics" element={<ChatbotAnalytics />} />
          <Route path="knowledge-base" element={<ChatbotKnowledgeBase />} />
          <Route path="transcript" element={<ChatbotTranscription />} />
        </Route>

        {/* Voice Agent Routes */}
        <Route path="voice-agent" element={<VoiceAgent />}>
          <Route index element={<Navigate to="knowledge-base" replace />} />
          <Route path="knowledge-base" element={<VoiceAgentKnowledgeBase />} />
          <Route path="phone-numbers" element={<PhoneNumbers />} />
          <Route path="batch-call" element={<BatchCall />} />
          <Route path="call-history" element={<CallHistory />} />
          <Route path="analytics" element={<Analytics />} />
        </Route>

        {/* Management Routes */}
        <Route path="user-management" element={<ClubAccountManagement />} />
        <Route path="admins-users" element={
          <PrivateRoute allowedRoles={['superadmin']}>
            <AdminList />
          </PrivateRoute>
        } />
        <Route path="club-management" element={<ClubManagement />} />
        <Route path="leads-management" element={<LeadsManagement />} />
        <Route path="documents" element={<DocumentsManagement />} />
        <Route path="widget-management" element={<WidgetManagement />} />
        <Route path="widget/:id" element={<WidgetPage />} />

        {/* Settings Routes */}
        <Route path="settings" element={<Settings />} />
        <Route path="api-keys" element={
          <PrivateRoute allowedRoles={['superadmin']}>
            <ApiKeysPage />
          </PrivateRoute>
        } />

        {/* Billing Routes */}
        <Route path="subscription-plans" element={<SubscriptionPlanManagement />} />
        <Route path="payments" element={<Payments />} />

        {/* Notification Routes */}
        <Route path="notifications" element={<AllNotifications />} />
        <Route path="notification-settings" element={<NotificationSettings />} />

        {/* Analytics Routes */}
        <Route path="analytics" element={<AnalyticsReports />} />
        <Route path="statistics" element={<Statistics />} />
      </Route>

      {/* Widget Routes - Removed separate admin/user routes as they are now handled by /widgets in App.jsx */}

      {/* Catch all route for 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;